import { useState, useEffect } from 'react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { analyticsService } from '@/services/analyticsService'
import toast from 'react-hot-toast'

interface NewUser {
  id: string
  fullName: string
  email: string
  photoURL: string
  createdAt: string
  accountType: string
  lastLogin: string | null
  displayName: string
}

interface ChartData {
  date: string
  fullDate: string
  count: number
}

export const NewUsersChart = () => {
  const [users, setUsers] = useState<NewUser[]>([])
  const [chartData, setChartData] = useState<ChartData[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedPeriod, setSelectedPeriod] = useState('7d')
  const [summary, setSummary] = useState<any>(null)

  useEffect(() => {
    const fetchNewUsers = async () => {
      try {
        setLoading(true)
        const data = await analyticsService.getNewUsers(selectedPeriod)
        
        setUsers(data.users)
        setSummary(data.summary)

        // Create chart data from users
        const usersByDate = new Map<string, number>()
        data.users.forEach(user => {
          const dateKey = new Date(user.createdAt).toISOString().split('T')[0]
          usersByDate.set(dateKey, (usersByDate.get(dateKey) || 0) + 1)
        })

        // Generate chart data for all dates in range
        const chartDataArray: ChartData[] = []
        const startDate = new Date(data.summary.dateRange.start)
        const endDate = new Date(data.summary.dateRange.end)
        
        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
          const dateKey = d.toISOString().split('T')[0]
          chartDataArray.push({
            date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            fullDate: dateKey,
            count: usersByDate.get(dateKey) || 0
          })
        }

        setChartData(chartDataArray)
      } catch (error: any) {
        console.error('Failed to fetch new users:', error)
        const errorMessage = error.response?.data?.error || 'Failed to load new users data'
        toast.error(errorMessage)
        setUsers([])
        setChartData([])
        setSummary(null)
      } finally {
        setLoading(false)
      }
    }

    fetchNewUsers()
  }, [selectedPeriod])

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">New Users</h3>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1"
          >
            <option value="7d">7d</option>
            <option value="30d">30d</option>
            <option value="90d">90d</option>
          </select>
        </div>
        <div className="h-48 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">New Users</h3>
          {summary && (
            <div className="flex items-center mt-1">
              <span className="text-2xl font-bold text-gray-900">
                {summary.totalUsers}
              </span>
              <span className="ml-2 text-sm font-medium text-gray-500">
                avg {summary.averageUsersPerDay}/day
              </span>
            </div>
          )}
        </div>
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-2 py-1"
        >
          <option value="7d">7d</option>
          <option value="30d">30d</option>
          <option value="90d">90d</option>
        </select>
      </div>

      {chartData.length > 0 ? (
        <div className="h-48 mb-6">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                label={{ value: 'Date', position: 'insideBottom', offset: -5, style: { fontSize: 12 } }}
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                label={{ value: 'Count', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
              />
              <Tooltip 
                formatter={(value: number) => [value.toLocaleString(), 'New Users']}
                labelFormatter={(label, payload) => {
                  if (payload && payload[0] && payload[0].payload.fullDate) {
                    const date = new Date(payload[0].payload.fullDate)
                    if (!isNaN(date.getTime())) {
                      return date.toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })
                    }
                  }
                  return label
                }}
                labelStyle={{ color: '#374151' }}
              />
              <Area 
                type="monotone" 
                dataKey="count" 
                stroke="#3b82f6" 
                fill="#3b82f6"
                fillOpacity={0.1}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-48 flex items-center justify-center text-gray-500 mb-6">
          No new users data available
        </div>
      )}

      {/* Recent Users List */}
      {users.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Recent Users</h4>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {users.map((user) => (
              <div key={user.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0">
                  {user.photoURL ? (
                    <img 
                      src={user.photoURL} 
                      alt={user.fullName}
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                      <span className="text-sm font-medium text-gray-600">
                        {user.fullName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user.fullName}
                  </p>
                  <p className="text-sm text-gray-500 truncate">
                    {user.email}
                  </p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="text-xs text-gray-500">
                    {new Date(user.createdAt).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
} 